import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { OktaAuthService } from '../services/okta-auth.service';
import { HybridStorageKeys, HybridStorageService } from '../app/services/hybrid-storage.service';

@Injectable({ providedIn: 'root' })
export class OktaAuthGuard implements CanActivate {
  // Prevent multiple concurrent guard checks - they run sequentially to avoid race conditions
  private isCheckInProgress: boolean = false;

  constructor(
    private oktaAuthService: OktaAuthService,
    private router: Router,
    private hybridStorageService: HybridStorageService) {}

  async canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    // If another activation check is already in progress, wait for it to complete before starting a new one
    // This prevents overlapping authentication checks but ensures each route gets its own check with correct parameters
    const maxWait = 5000; // 5 second timeout to prevent infinite wait
    let waited = 0;

    while (this.isCheckInProgress && waited < maxWait) {
      console.warn('[OktaAuthGuard] Concurrent activation check detected, waiting for previous check to complete...');
      await new Promise(resolve => setTimeout(resolve, 50));
      waited += 50;
    }

    if (this.isCheckInProgress) {
      console.error('[OktaAuthGuard] Previous check did not complete within timeout, proceeding anyway');
      // Don't set isCheckInProgress = true here to avoid compounding the problem
      // Just proceed with the check
      return await this.performActivationCheck(_route, state);
    }

    // Mark check as in progress
    this.isCheckInProgress = true;

    try {
      return await this.performActivationCheck(_route, state);
    } finally {
      // Clear the flag when done so next check can proceed
      this.isCheckInProgress = false;
    }
  }

  private async performActivationCheck(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    // Wait for tokens to be initialized before checking - prevents race condition
    // where guard checks before storage has finished loading tokens from keychain/localStorage
    await this.oktaAuthService.waitForTokensInitialized();

    const oktaAuth = this.oktaAuthService.getOktaAuth();
    const isAuthenticated = await oktaAuth.isAuthenticated();

    if (isAuthenticated) {
      return true;
    }

    // Access token missing or expired. Try to refresh it before letting the route activate —
    // otherwise the component's first API call goes out with the stale Bearer and returns 401.
    const refreshToken = await oktaAuth.tokenManager.get('refreshToken');
    const hasValidRefreshToken = refreshToken && !oktaAuth.tokenManager.hasExpired(refreshToken);

    if (hasValidRefreshToken) {
      await this.oktaAuthService.ensureFreshAccessToken();
      // Allow access whether or not the refresh succeeded: on success the route gets a
      // fresh token; on failure (offline / network) we fall back to cached-data offline mode.
      return true;
    }

    // No valid tokens - redirect to login
    const attemptedUrl = state.url;
    // Only store if not already set by DeepLinkService or AppComponent
    const pending = await this.hybridStorageService.getItem<string>(HybridStorageKeys.Capacitor_DeepLinkPath);
    if (!pending && attemptedUrl && attemptedUrl !== '/' && attemptedUrl !== '/login' && attemptedUrl !== '/callback') {
      await this.hybridStorageService.setItem(HybridStorageKeys.Capacitor_DeepLinkPath, attemptedUrl);
    }
    await this.oktaAuthService.handleAuthRequired(this.router);
    return false;
  }
}